import { useEffect, useMemo, useState } from 'react';
import { ChevronRight, KeyRound } from 'lucide-react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { toast } from 'react-toastify';
import camiyuiLogo from '../../assets/camiyui-logo.jpg';
import { STORE_DISPLAY_NAME } from '../../constants/branding';
import InstallPrompt from '../../components/InstallPrompt';
import LoginNotificationPrompt from '../../components/LoginNotificationPrompt';
import PasswordInput from '../../components/PasswordInput';
import {
  completeCustomerPasswordReset,
  resolveCustomerPasswordReset,
} from '../../utils/login/migration';

const ResetPasswordPage = () => {
  const [searchParams] = useSearchParams();
  const token = useMemo(() => searchParams.get('token')?.trim() ?? '', [searchParams]);
  const navigate = useNavigate();

  const [loading, setLoading] = useState(true);
  const [customerName, setCustomerName] = useState('');
  const [minPasswordLength, setMinPasswordLength] = useState(8);
  const [password, setPassword] = useState('');
  const [passwordConfirm, setPasswordConfirm] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    let alive = true;

    if (!token) {
      setError('This password reset link is invalid.');
      setLoading(false);
      return () => {
        alive = false;
      };
    }

    void (async () => {
      const result = await resolveCustomerPasswordReset(token);
      if (!alive) return;

      if (!result.ok) {
        setError(result.message);
        setLoading(false);
        return;
      }

      setCustomerName(result.data.customer_name);
      setMinPasswordLength(result.data.min_password_length || 8);
      setLoading(false);
    })();

    return () => {
      alive = false;
    };
  }, [token]);

  const handleResetPassword = async () => {
    if (password.trim().length < minPasswordLength) {
      toast.warning(`Please use at least ${minPasswordLength} characters for your password.`);
      return;
    }

    if (password !== passwordConfirm) {
      toast.warning('The password confirmation does not match.');
      return;
    }

    setIsSubmitting(true);
    try {
      const result = await completeCustomerPasswordReset(token, password);
      if (!result.ok) {
        if (result.status === 404 || result.status === 410 || result.status === 409) {
          toast.info(result.message);
          if (result.status !== 409) {
            setError(result.message);
          }
          return;
        }
        toast.error(result.message);
        return;
      }

      toast.success('Your password has been updated. Please log in again.');
      navigate('/login', { replace: true });
    } finally {
      setIsSubmitting(false);
    }
  };

  if (loading) {
    return <div className="flex min-h-screen items-center justify-center bg-slate-50 text-slate-600">Loading…</div>;
  }

  if (error) {
    return (
      <div className="flex min-h-screen items-center justify-center bg-slate-50 px-4">
        <div className="max-w-md rounded-3xl border border-red-100 bg-white p-8 text-center shadow-sm">
          <h1 className="text-xl font-semibold text-slate-950">Password reset unavailable</h1>
          <p className="mt-3 text-sm leading-6 text-slate-500">{error}</p>
          <button
            type="button"
            onClick={() => navigate('/login')}
            className="mt-6 rounded-full bg-slate-900 px-5 py-3 text-sm font-medium text-white hover:bg-slate-800"
          >
            Back to Login
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[radial-gradient(circle_at_top,#e8f5f1_0%,#f8fafc_42%,#eef5f8_100%)] px-4 py-8">
      <LoginNotificationPrompt />

      <main className="mx-auto max-w-md">
        <section className="mb-5 px-2 pt-3 pb-1">
          <div className="flex items-center justify-center gap-3">
            <div className="h-14 w-14 overflow-hidden rounded-[18px] border border-white/70 bg-white shadow-[0_12px_30px_rgba(15,23,42,0.08)]">
              <img src={camiyuiLogo} alt={STORE_DISPLAY_NAME} className="h-full w-full object-cover" />
            </div>
            <div>
              <p className="text-[11px] font-semibold uppercase tracking-[0.24em] text-emerald-700">
                {STORE_DISPLAY_NAME}
              </p>
              <h1 className="mt-1 text-[28px] font-semibold tracking-[-0.03em] text-slate-950">
                Reset Password
              </h1>
            </div>
          </div>
        </section>

        <section className="overflow-hidden rounded-[32px] border border-emerald-100 bg-white/90 shadow-lg shadow-emerald-100/60 backdrop-blur">
          <div className="border-b border-emerald-100/80 bg-white px-5 py-5">
            <h2 className="text-xl font-semibold tracking-[-0.02em] text-slate-950">
              {customerName || 'Customer'} password reset
            </h2>
            <p className="mt-2 text-sm leading-6 text-slate-500">
              Choose a new password for this customer app account.
            </p>
          </div>

          <div className="space-y-5 p-5">
            <div className="space-y-3">
              <div className="flex items-center gap-2 text-sm font-semibold text-slate-700">
                <KeyRound className="h-4 w-4 text-emerald-700" />
                New Password
              </div>
              <PasswordInput
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                placeholder={`At least ${minPasswordLength} characters`}
                autoComplete="new-password"
                className="w-full rounded-[22px] border border-slate-200 bg-slate-50 py-4 pl-4 pr-14 text-base text-slate-900 outline-none transition focus:border-emerald-500 focus:bg-white focus:ring-2 focus:ring-emerald-100"
              />
              <PasswordInput
                value={passwordConfirm}
                onChange={(e) => setPasswordConfirm(e.target.value)}
                placeholder="Confirm password"
                autoComplete="new-password"
                className="w-full rounded-[22px] border border-slate-200 bg-slate-50 py-4 pl-4 pr-14 text-base text-slate-900 outline-none transition focus:border-emerald-500 focus:bg-white focus:ring-2 focus:ring-emerald-100"
              />
              <p className="text-xs leading-5 text-slate-500">
                After saving this password, you can log in again with any login-enabled phone number on this account.
              </p>
            </div>

            <button
              type="button"
              onClick={handleResetPassword}
              disabled={isSubmitting}
              className="flex min-h-[56px] w-full items-center justify-center gap-2 rounded-[22px] bg-slate-950 px-4 py-4 text-base font-semibold text-white transition hover:bg-slate-800 disabled:cursor-not-allowed disabled:opacity-60"
            >
              Update Password
              <ChevronRight className="h-5 w-5" />
            </button>
          </div>
        </section>

        <div className="mt-6">
          <InstallPrompt mode="embedded" />
        </div>
      </main>
    </div>
  );
};

export default ResetPasswordPage;
